import { useState, useEffect } from 'react';
import {
  ArrowLeft, Wrench, Mail, Calendar, Search as SearchIcon,
  Code, Database,
  ToggleLeft, ToggleRight,
  RefreshCw, ChevronRight, Lock
} from 'lucide-react';
import { api } from '../lib/api';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

interface McpTool {
  id: string;
  name: string;
  description: string;
  category: string;
  status: 'available' | 'connected' | 'restricted';
  inputSchema?: Record<string, unknown>;
  requiredScopes?: string[];
}

interface McpToolsPanelProps {
  replicaId: string;
  onBack: () => void;
}

// ═══════════════════════════════════════════════════════════════════════
// MOCK DATA
// ═══════════════════════════════════════════════════════════════════════

const MOCK_TOOLS: McpTool[] = [
  { id: 'gmail', name: 'Gmail', description: 'Read, draft, and send emails via Gmail API', category: 'communication', status: 'available', requiredScopes: ['gmail.readonly', 'gmail.send'] },
  { id: 'calendar', name: 'Google Calendar', description: 'View and manage calendar events, check availability', category: 'productivity', status: 'available', requiredScopes: ['calendar.readonly', 'calendar.events'] },
  { id: 'drive', name: 'Google Drive', description: 'Search, read, and organize documents in Drive', category: 'storage', status: 'available', requiredScopes: ['drive.readonly'] },
  { id: 'web-search', name: 'Web Search', description: 'Search the web for real-time information', category: 'search', status: 'connected' },
  { id: 'code-sandbox', name: 'Code Sandbox', description: 'Execute code in a secure sandboxed environment', category: 'development', status: 'restricted', requiredScopes: ['sandbox.execute'] },
  { id: 'slack', name: 'Slack', description: 'Read and send messages in Slack workspaces', category: 'communication', status: 'available', requiredScopes: ['channels:read', 'chat:write'] },
  { id: 'notion', name: 'Notion', description: 'Read and update Notion pages and databases', category: 'productivity', status: 'available', requiredScopes: ['read_content', 'update_content'] },
  { id: 'github', name: 'GitHub', description: 'Access repositories, issues, and pull requests', category: 'development', status: 'available', requiredScopes: ['repo', 'issues'] },
];

// ═══════════════════════════════════════════════════════════════════════
// CATEGORY CONFIG
// ═══════════════════════════════════════════════════════════════════════

const categoryConfig: Record<string, { icon: typeof Mail; color: string; bg: string }> = {
  communication: { icon: Mail, color: 'text-blue-400', bg: 'from-blue-500/20 to-blue-600/20' },
  productivity: { icon: Calendar, color: 'text-emerald-400', bg: 'from-emerald-500/20 to-teal-500/20' },
  storage: { icon: Database, color: 'text-purple-400', bg: 'from-purple-500/20 to-violet-500/20' },
  search: { icon: SearchIcon, color: 'text-cyan-400', bg: 'from-cyan-500/20 to-blue-500/20' },
  development: { icon: Code, color: 'text-orange-400', bg: 'from-orange-500/20 to-amber-500/20' },
};

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  available: { label: 'Available', color: 'text-hig-text-tertiary', bg: 'bg-hig-bg-tertiary' },
  connected: { label: 'Connected', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  restricted: { label: 'Restricted', color: 'text-amber-400', bg: 'bg-amber-500/10' },
};

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export default function McpToolsPanel({ replicaId, onBack }: McpToolsPanelProps) {
  const [tools, setTools] = useState<McpTool[]>(MOCK_TOOLS);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [expandedTool, setExpandedTool] = useState<string | null>(null);
  const [toggledTools, setToggledTools] = useState<Set<string>>(new Set(['web-search']));

  // Fetch real tools
  useEffect(() => {
    setLoading(true);
    api.get<{ success: boolean; tools: McpTool[] }>(`/mcp-tools/${replicaId}`)
      .then(res => {
        if (res.data?.success && res.data.tools?.length) {
          setTools(res.data.tools);
        }
      })
      .catch(() => { /* mock fallback */ })
      .finally(() => setLoading(false));
  }, [replicaId]);

  const handleToggle = (toolId: string) => {
    setToggledTools(prev => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId);
      else next.add(toolId);
      return next;
    });
  };

  const categories = ['all', ...new Set(tools.map(t => t.category))];
  const filtered = filter === 'all' ? tools : tools.filter(t => t.category === filter);
  const connectedCount = toggledTools.size;

  return (
    <div className="max-w-4xl mx-auto space-y-5 p-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-xl transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-hig-text-primary flex items-center gap-2">
            <Wrench size={20} className="text-orange-400" />
            MCP Tool Gateway
          </h1>
          <p className="text-xs text-hig-text-tertiary">
            {tools.length} tools available · {connectedCount} connected
          </p>
        </div>
        {loading && <RefreshCw size={16} className="animate-spin text-hig-text-tertiary" />}
      </div>

      {/* Category filters */}
      <div className="flex gap-2 flex-wrap">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${
              filter === cat
                ? 'bg-hig-accent/15 text-hig-accent border border-hig-accent/30'
                : 'bg-hig-bg-tertiary/50 text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Tool grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map(tool => {
          const catConf = categoryConfig[tool.category] || categoryConfig.search;
          const statConf = statusConfig[tool.status] || statusConfig.available;
          const CatIcon = catConf.icon;
          const isEnabled = toggledTools.has(tool.id);
          const isExpanded = expandedTool === tool.id;

          return (
            <div
              key={tool.id}
              className={`card overflow-hidden transition-all duration-300 ${isEnabled ? 'ring-1 ring-hig-accent/20' : ''}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${catConf.bg} flex items-center justify-center flex-shrink-0`}>
                      <CatIcon size={18} className={catConf.color} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-sm font-semibold text-hig-text-primary">{tool.name}</h3>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statConf.bg} ${statConf.color} font-medium`}>
                          {statConf.label}
                        </span>
                      </div>
                      <p className="text-xs text-hig-text-secondary leading-relaxed">{tool.description}</p>
                    </div>
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(tool.id)}
                    disabled={tool.status === 'restricted'}
                    className={`flex-shrink-0 transition-colors ${tool.status === 'restricted' ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                    title={tool.status === 'restricted' ? 'Restricted — requires elevated permissions' : isEnabled ? 'Disable' : 'Enable'}
                  >
                    {isEnabled ? (
                      <ToggleRight size={28} className="text-hig-accent" />
                    ) : (
                      <ToggleLeft size={28} className="text-hig-text-tertiary" />
                    )}
                  </button>
                </div>

                {/* Expand button */}
                <button
                  onClick={() => setExpandedTool(isExpanded ? null : tool.id)}
                  className="mt-2.5 flex items-center gap-1 text-[11px] text-hig-text-tertiary hover:text-hig-text-secondary transition-colors"
                >
                  <ChevronRight size={11} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  Details
                </button>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="border-t border-hig-divider p-4 bg-hig-bg-tertiary/20 animate-fade-in space-y-3">
                  {tool.requiredScopes && (
                    <div>
                      <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wider mb-1.5">Required Scopes</p>
                      <div className="flex flex-wrap gap-1">
                        {tool.requiredScopes.map(scope => (
                          <span key={scope} className="text-[10px] px-1.5 py-0.5 rounded bg-hig-bg-tertiary text-hig-text-secondary font-mono">
                            {scope}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wider mb-1.5">Category</p>
                    <p className="text-xs text-hig-text-primary capitalize">{tool.category}</p>
                  </div>
                  {tool.status === 'restricted' && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/5 border border-amber-500/15">
                      <Lock size={12} className="text-amber-400" />
                      <p className="text-[11px] text-amber-400">Requires sandbox policy elevation to enable</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="card p-8 text-center">
          <Wrench size={32} className="mx-auto text-hig-text-tertiary mb-3 opacity-30" />
          <p className="text-sm text-hig-text-secondary">No tools in this category</p>
        </div>
      )}
    </div>
  );
}
